import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowUp, ArrowDown, Save } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, money, productImage } from "@/lib/api";
import { Field, SectionTitle, Thumb, SelectionBadge } from "@/components/Bits";
import PresentationVisuals from "@/components/PresentationVisuals";
import PresentationComposer from "@/components/PresentationComposer";
import ProjectNav from "@/components/ProjectNav";
import useUnsavedGuard from "@/hooks/useUnsavedGuard";

const input = "w-full rounded-sm border border-line bg-white px-4 py-2.5 text-sm outline-none focus:border-lime";
const STATUSES = ["Draft", "Ready to Publish", "Published", "Revision Requested", "Approved", "Archived"];

export default function PresentationForm() {
  const { id } = useParams();
  const editing = Boolean(id);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [baseline, setBaseline] = useState("");
  const [visuals, setVisuals] = useState([]);
  const [form, setForm] = useState({
    name: "", project_id: params.get("project") || "", space_id: params.get("space") || "",
    title: "", narrative: "", hero_image: "", moodboard_image: "", rendering_image: "",
    floor_plan_image: "", selection_ids: [], status: "Draft", approval_mode: "individual",
    visible_to_client: false,
  });

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    if (editing) api.get(`/presentations/${id}`).then((r) => {
      setForm((f) => ({ ...f, ...r.data }));
      setBaseline(JSON.stringify(r.data));
      setVisuals(r.data.visuals || []);
    }).catch((e) => toast.error(apiError(e)));
  }, [id, editing]);

  useEffect(() => {
    if (!form.project_id) return;
    api.get(`/projects/${form.project_id}/spaces`).then((r) => setSpaces(r.data)).catch(() => setSpaces([]));
    api.get("/products", { params: { project_id: form.project_id } }).then((r) => setProducts(r.data)).catch(() => setProducts([]));
  }, [form.project_id]);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const available = useMemo(
    () => products.filter((p) => !form.space_id || p.space_id === form.space_id),
    [products, form.space_id]
  );
  const chosen = form.selection_ids.map((sid) => products.find((p) => p.id === sid)).filter(Boolean);
  const dirty = useMemo(
    () => (editing ? baseline !== "" && JSON.stringify({ ...JSON.parse(baseline || "{}"), ...form }) !== baseline : Boolean(form.name || form.title || form.narrative)),
    [form, baseline, editing]
  );
  const guardLeave = useUnsavedGuard(dirty);

  const toggle = (pid) =>
    setForm((f) => ({
      ...f,
      selection_ids: f.selection_ids.includes(pid) ? f.selection_ids.filter((x) => x !== pid) : [...f.selection_ids, pid],
    }));

  const move = (idx, dir) =>
    setForm((f) => {
      const ids = [...f.selection_ids];
      const target = idx + dir;
      if (target < 0 || target >= ids.length) return f;
      [ids[idx], ids[target]] = [ids[target], ids[idx]];
      return { ...f, selection_ids: ids };
    });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = editing
        ? await api.put(`/presentations/${id}`, form)
        : await api.post("/presentations", form);
      toast.success(editing ? "Presentation saved" : "Presentation created");
      navigate(editing ? `/presentations/${data.id}` : `/presentations/${data.id}/edit`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-10 pb-10" data-testid="presentation-form">
      <ProjectNav
        projectId={form.project_id || null}
        projectName={projects.find((p) => p.id === form.project_id)?.name || "Project"}
        activeSection="Presentations"
        onLeave={guardLeave}
        trail={[
          ...(form.project_id
            ? [{ label: "Presentations", to: `/projects/${form.project_id}?tab=Presentations` }]
            : []),
          { label: editing ? "Edit Presentation" : "New Presentation" },
        ]}
      />
      {form.project_id && (
        <Link
          to={`/projects/${form.project_id}?tab=Presentations`}
          onClick={guardLeave}
          data-testid="back-to-presentations"
          className="inline-flex items-center gap-2 rounded-sm border border-line bg-white px-4 py-2 text-xs uppercase tracking-[0.12em] text-stone transition-colors hover:border-lime hover:text-ink"
        >
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} /> Back to Presentation Overview
        </Link>
      )}

      <div className="flex flex-col gap-5 border-b border-line pb-8 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="overline mb-3">Design Presentations</div>
          <h1 className="font-serif text-3xl tracking-tight text-ink sm:text-4xl">
            {editing ? "Edit Presentation" : "Create Presentation"}
          </h1>
        </div>
        <button data-testid="presentation-save-btn" type="submit" disabled={busy} className="inline-flex items-center gap-2 rounded-sm bg-lime px-5 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark disabled:opacity-60">
          <Save className="h-4 w-4" strokeWidth={1.5} /> {busy ? "Saving…" : "Save Presentation"}
        </button>
      </div>

      <section className="card-surface p-7">
        <SectionTitle>Presentation Details</SectionTitle>
        <div className="grid gap-6 md:grid-cols-2">
          <Field label="Presentation Name">
            <input data-testid="presentation-name-input" required value={form.name} onChange={set("name")} placeholder="Living Room — Design Presentation 01" className={input} />
          </Field>
          <Field label="Client-Facing Title">
            <input data-testid="presentation-title-input" value={form.title || ""} onChange={set("title")} placeholder="Living Room" className={input} />
          </Field>
          <Field label="Project">
            <select data-testid="presentation-project-select" required value={form.project_id} onChange={set("project_id")} className={input}>
              <option value="">Select project</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </Field>
          <Field label="Space">
            <select data-testid="presentation-space-select" value={form.space_id || ""} onChange={set("space_id")} className={input}>
              <option value="">— Whole project —</option>
              {spaces.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="Design Narrative" className="md:col-span-2">
            <textarea data-testid="presentation-narrative-input" rows={4} value={form.narrative || ""} onChange={set("narrative")} className={input} />
          </Field>
          {[["hero_image", "Cover Image URL (optional)"]].map(([k, l]) => (
            <Field key={k} label={l} hint="Optional — uploading visuals below and setting a cover is the preferred workflow.">
              <input data-testid={`presentation-${k.replace(/_/g, "-")}-input`} value={form[k] || ""} onChange={set(k)} className={input} />
            </Field>
          ))}
          <label className="flex items-center gap-3 text-sm text-ink sm:col-span-2">
            <input
              data-testid="presentation-show-selections-toggle"
              type="checkbox"
              checked={form.show_selections_block !== false}
              onChange={(e) => setForm({ ...form, show_selections_block: e.target.checked })}
              className="h-4 w-4 accent-[#BEC221]"
            />
            Show the selections block to the client in this presentation
          </label>
          <Field label="Presentation Status">
            <select data-testid="presentation-status-select" value={form.status} onChange={set("status")} className={input}>
              {STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Approval Mode" hint="Whole-design approval approves every selection awaiting approval.">
            <select data-testid="presentation-approval-mode-select" value={form.approval_mode} onChange={set("approval_mode")} className={input}>
              <option value="individual">Individual selection approval</option>
              <option value="whole">Approve entire presentation</option>
              <option value="both">Both</option>
            </select>
          </Field>
        </div>
        <label className="mt-7 flex items-center gap-3 text-sm text-ink">
          <input data-testid="presentation-visible-toggle" type="checkbox" checked={!!form.visible_to_client} onChange={set("visible_to_client")} className="h-4 w-4 accent-[#BEC221]" />
          Visible to client (publishing also enables this)
        </label>
      </section>

      {editing ? (
        <>
          <PresentationVisuals
            presentationId={id}
            projectId={form.project_id}
            initial={visuals}
            pinterestBoard={form.pinterest_board}
            onChange={setVisuals}
          />
          <PresentationComposer
            presentationId={id}
            visuals={visuals}
            selections={chosen}
            initial={form.blocks || []}
          />
        </>
      ) : (
        <section className="card-surface p-7" data-testid="presentation-visuals-hint">
          <SectionTitle>Presentation Visuals</SectionTitle>
          <p className="max-w-xl text-sm leading-relaxed text-stone">
            Save this presentation and you’ll land straight on the visuals composer — upload
            renderings, moodboards, floor plans and elevations directly from your computer, no
            hosting required.
          </p>
        </section>
      )}

      <section className="card-surface p-7">
        {chosen.length > 0 && (
          <div className="mb-8 space-y-3" data-testid="presentation-chosen-list">
            {chosen.map((p, i) => (
              <div key={p.id} className="flex items-center gap-4 rounded-sm border border-line p-3">
                <Thumb src={productImage(p)} className="h-12 w-14" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm text-ink">{p.name}</div>
                  <div className="num text-xs text-fog">{money(p.client_total)} · {p.space_name || "Unassigned"}</div>
                </div>
                <SelectionBadge status={p.selection_status} />
                <div className="flex gap-1">
                  <button type="button" data-testid={`presentation-move-up-${p.id}`} onClick={() => move(i, -1)} className="rounded-sm border border-line p-1.5 text-stone hover:border-lime">
                    <ArrowUp className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </button>
                  <button type="button" data-testid={`presentation-move-down-${p.id}`} onClick={() => move(i, 1)} className="rounded-sm border border-line p-1.5 text-stone hover:border-lime">
                    <ArrowDown className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </button>
                  <button type="button" data-testid={`presentation-remove-${p.id}`} onClick={() => toggle(p.id)} className="rounded-sm border border-line px-2 text-xs text-clay hover:border-clay">Remove</button>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="overline mb-4">Available project selections</div>
        <div className="grid gap-3 sm:grid-cols-2" data-testid="presentation-available-list">
          {available.filter((p) => !form.selection_ids.includes(p.id)).map((p) => (
            <button
              key={p.id}
              type="button"
              data-testid={`presentation-add-selection-${p.id}`}
              onClick={() => toggle(p.id)}
              className="flex items-center gap-4 rounded-sm border border-line p-3 text-left transition-colors hover:border-lime hover:bg-[#EDEFC4]/40"
            >
              <Thumb src={productImage(p)} className="h-12 w-14" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm text-ink">{p.name}</div>
                <div className="num text-xs text-fog">{money(p.client_total)} · {p.space_name || "Unassigned"}</div>
              </div>
            </button>
          ))}
          {!available.length && <p className="text-sm text-fog">No selections available for this project/space.</p>}
        </div>
        <p className="mt-5 text-[11px] leading-relaxed text-fog">
          Presentations reference existing project selections — nothing is duplicated, and procurement records stay intact.
        </p>
      </section>
    </form>
  );
}
